import React from 'react';
import { AlertTriangle, MessageSquare, Plus } from 'lucide-react';
import { useSmsQuota, useSmsPricing } from '../hooks/useSmsQuota';

export default function SmsQuotaWidget({ onRechargeClick }) {
  const { data: quotaData, isLoading: isQuotaLoading, error: quotaError } = useSmsQuota();
  const { data: pricingData, isLoading: isPricingLoading } = useSmsPricing();

  if (isQuotaLoading || isPricingLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (quotaError) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-red-600 text-center">
          Failed to load SMS quota information
        </div>
      </div>
    );
  }

  const quota = quotaData?.data;
  const pricing = pricingData?.data;

  if (!quota) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-gray-600 text-center">
          No SMS quota information available
        </div>
      </div>
    );
  }

  const percentageRemaining = quota.allocated > 0 ? (quota.remaining / quota.allocated) * 100 : 0;
  // Clamp to 0–100 to avoid visual overflow of the progress bar when data is slightly off
  const clampedPercentage = Math.min(Math.max(percentageRemaining, 0), 100);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 border border-gray-100 dark:border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <MessageSquare className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
          SMS Quota
        </h3>
        {pricing && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {pricing.price_per_sms} {pricing.currency} per SMS
          </span>
        )}
      </div>

      {/* Quota Status */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Remaining SMS</span>
          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {quota.remaining} / {quota.allocated}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2 overflow-hidden">
          <div 
            className={`h-3 rounded-full transition-all duration-300 ${
              quota.is_exhausted 
                ? 'bg-red-500' 
                : quota.is_low 
                ? 'bg-yellow-500' 
                : 'bg-green-500'
            }`}
            style={{ width: `${clampedPercentage}%` }}
          ></div>
        </div>
        
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Used: {quota.used}</span>
          <span>Remaining: {quota.remaining}</span>
        </div>
      </div>

      {/* Warning Alert */}
      {quota.is_low && !quota.is_exhausted && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/40 border border-yellow-200 dark:border-yellow-500/60 rounded-md">
          <div className="flex items-center">
            <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-300 mr-2" />
            <span className="text-sm text-yellow-800 dark:text-yellow-100">
              Low SMS quota! You have {quota.remaining} SMS remaining.
            </span>
          </div>
        </div>
      )}

      {quota.is_exhausted && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-500/70 rounded-md">
          <div className="flex items-center">
            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-300 mr-2" />
            <span className="text-sm text-red-800 dark:text-red-100">
              SMS quota exhausted! Please recharge to continue sending SMS.
            </span>
          </div>
        </div>
      )}

      {/* Recharge Button */}
      <button
        onClick={onRechargeClick}
        className={`w-full flex items-center justify-center px-4 py-2 rounded-md font-medium transition-colors ${
          quota.is_low || quota.is_exhausted
            ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-500 dark:hover:bg-blue-400'
            : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:bg-green-500 dark:hover:bg-green-400'
        }`}
      >
        <Plus className="w-4 h-4 mr-2" />
        {quota.is_exhausted ? 'Recharge SMS' : 'Add More SMS'}
      </button>

      {/* Pricing Info */}
      {pricing && (
        <div className="mt-4 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg bg-gray-50 dark:bg-gray-800/60">
          <div className="text-xs text-gray-500 dark:text-gray-300 space-y-1">
            <div>Minimum recharge: {pricing.minimum_recharge} SMS</div>
            <div>Price per SMS: {pricing.price_per_sms} {pricing.currency}</div>
          </div>
        </div>
      )}
    </div>
  );
} 